import React, { useState, useEffect } from 'react'
import { connect } from 'dva'
import PropTypes from 'prop-types'

import { clearSvgCache, setCachingEnabled, isCachingEnabled } from '../../../lib/svgCache'
import { getCacheMetrics } from '../../../lib/cacheMonitor'
import SidebarBody from '../SidebarBody'
import SidebarDetailHeader from '../SidebarDetailHeader'
import styles from './MemoryMonitor.module.scss'

function MemoryMonitorSidebar(props) {
  // Prevent rendering in production - same check as ToolBar
  if (!props.svgCachingEnabled || !props.memoryMonitorEnabled) {
    return <NoMetricsAvailable />
  }

  const [cacheDetailsExpanded, setCacheDetailsExpanded] = useState(false)
  const [cachingEnabled, setCachingEnabledState] = useState(true)
  const [loading, setLoading] = useState(true)
  const [metrics, setMetrics] = useState(null)

  useEffect(() => {
    // Update metrics every second
    const interval = setInterval(() => {
      const newMetrics = getCacheMetrics()
      if (newMetrics) {
        setMetrics(newMetrics)
      }
      setLoading(false)
    }, 1000)

    // Initial load
    const initialMetrics = getCacheMetrics()
    setMetrics(initialMetrics)
    setCachingEnabledState(isCachingEnabled())

    return () => clearInterval(interval)
  }, [])

  const handleClearCache = () => {
    clearSvgCache()
    const newMetrics = getCacheMetrics()
    setMetrics(newMetrics)
  }

  const handleToggleCaching = () => {
    const newState = !cachingEnabled
    setCachingEnabled(newState)
    setCachingEnabledState(newState)
    // Update metrics after toggling
    const newMetrics = getCacheMetrics()
    setMetrics(newMetrics)
  }

  const formatBytes = bytes => {
    if (bytes === null || bytes === undefined) return '0 B'
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return Math.round((bytes / 1024) * 100) / 100 + ' KB'
    return Math.round((bytes / 1024 / 1024) * 100) / 100 + ' MB'
  }

  const formatTime = timestamp => {
    if (!timestamp) return 'N/A'
    try {
      const date = new Date(timestamp)
      return date.toLocaleTimeString()
    } catch (e) {
      return 'N/A'
    }
  }

  if (loading) {
    return <LoadingMetrics />
  }

  if (!metrics) {
    return <NoMetricsAvailable />
  }

  const clientMemory = metrics?.memory?.client
  const serverMemory = metrics?.memory?.server
  const cache = metrics?.cache

  return (
    <>
      <SidebarDetailHeader title="Memory Monitor" />
      <SidebarBody className={styles.memoryMonitorSidebar}>
        {/* Client Side Memory */}
        {clientMemory && (
          <div className={styles.section}>
            <div className={styles.sectionHeader + ' ' + styles.clientHeader}>
              <span>Client Side</span>
            </div>
            <div className={styles.metrics}>
              <div className={styles.metric}>
                <span className={styles.label}>Used:</span>
                <span className={styles.value}>{clientMemory.used} MB</span>
              </div>
              <div className={styles.metric}>
                <span className={styles.label}>Total:</span>
                <span className={styles.value}>{clientMemory.total} MB</span>
              </div>
              <div className={styles.metric}>
                <span className={styles.label}>Limit:</span>
                <span className={styles.value}>{clientMemory.limit} MB</span>
              </div>
              <div className={styles.metric}>
                <span className={styles.label}>Usage:</span>
                <span className={styles.value + ' ' + styles.usageValue}>{clientMemory.usage}%</span>
              </div>
            </div>
          </div>
        )}

        {/* Server Side Memory */}
        {serverMemory && (
          <div className={styles.section}>
            <div className={styles.sectionHeader + ' ' + styles.serverHeader}>
              <span>Server Side</span>
            </div>
            <div className={styles.metrics}>
              <div className={styles.metric}>
                <span className={styles.label}>RSS:</span>
                <span className={styles.value}>{serverMemory.rss} MB</span>
              </div>
              <div className={styles.metric}>
                <span className={styles.label}>Heap Total:</span>
                <span className={styles.value}>{serverMemory.heapTotal} MB</span>
              </div>
              <div className={styles.metric}>
                <span className={styles.label}>Heap Used:</span>
                <span className={styles.value}>{serverMemory.heapUsed} MB</span>
              </div>
              <div className={styles.metric}>
                <span className={styles.label}>External:</span>
                <span className={styles.value}>{serverMemory.external} MB</span>
              </div>
              <div className={styles.metric}>
                <span className={styles.label}>ArrayBuffers:</span>
                <span className={styles.value}>{serverMemory.arrayBuffers} MB</span>
              </div>
              <div className={styles.metric}>
                <span className={styles.label}>Heap Usage:</span>
                <span className={styles.value + ' ' + styles.usageValue}>{serverMemory.heapUsage}%</span>
              </div>
            </div>
          </div>
        )}

        {/* SVG Cache Information */}
        {cache && (
          <div className={styles.section}>
            <div className={styles.sectionHeader + ' ' + styles.cacheHeader}>
              <span>SVG Cache</span>
              <div className={styles.cacheControls}>
                <button
                  className={cachingEnabled ? styles.toggleButton : styles.toggleButtonDisabled}
                  onClick={handleToggleCaching}
                  title={cachingEnabled ? 'Disable Caching' : 'Enable Caching'}
                >
                  {cachingEnabled ? 'ON' : 'OFF'}
                </button>
                <button className={styles.clearButton} onClick={handleClearCache} title="Clear SVG Cache">
                  Clear
                </button>
              </div>
            </div>
            <div className={styles.metrics}>
              <div className={styles.metric}>
                <span className={styles.label}>Cache Status:</span>
                <span className={styles.value + ' ' + (cache.cachingEnabled ? styles.statusEnabled : styles.statusDisabled)}>
                  {cache.cachingEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className={styles.metric}>
                <span className={styles.label}>Cache Size:</span>
                <span className={styles.value}>{cache.svgCacheSize} items</span>
              </div>
              <div className={styles.metric}>
                <span className={styles.label}>Total Size:</span>
                <span className={styles.value}>{cache.totalSizeMB} MB</span>
              </div>
              <div className={styles.metric}>
                <span className={styles.label}>Active Blob URLs:</span>
                <span className={styles.value}>{cache.activeBlobUrls}</span>
              </div>
              {cache.nonCachedBlobUrls !== undefined && cache.nonCachedBlobUrls > 0 && (
                <div className={styles.metric}>
                  <span className={styles.label}>Pending Cleanup:</span>
                  <span className={styles.value}>{cache.nonCachedBlobUrls}</span>
                </div>
              )}
              {cache.cacheDetails && cache.cacheDetails.length > 0 && (
                <div className={styles.cacheDetails}>
                  <button
                    className={styles.toggleDetails}
                    onClick={() => setCacheDetailsExpanded(!cacheDetailsExpanded)}
                  >
                    {cacheDetailsExpanded ? '▼' : '▶'} Cache Details ({cache.cacheDetails.length} items)
                  </button>
                  {cacheDetailsExpanded && (
                    <div className={styles.detailsList}>
                      {cache.cacheDetails.map((detail, index) => (
                        <div key={index} className={styles.detailItem}>
                          <div className={styles.detailKey}>
                            <strong>{detail.displayLabel != null ? detail.displayLabel : detail.key}</strong>
                          </div>
                          <div className={styles.detailInfo}>
                            Size: {formatBytes(detail.size)} | Accesses: {detail.accessCount} | Created: {formatTime(detail.createdAt)} | Last:{' '}
                            {formatTime(detail.lastAccessed)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </SidebarBody>
    </>
  )
}

MemoryMonitorSidebar.propTypes = {
  memoryMonitorEnabled: PropTypes.bool,
  svgCachingEnabled: PropTypes.bool,
}

export default connect((model) => {
  return {
    memoryMonitorEnabled: model.app.config.MEMORY_MONITOR_ENABLED === true,
    svgCachingEnabled: model.app.config.SVG_CACHING_ENABLED === true,
  }
})(MemoryMonitorSidebar)

function LoadingMetrics() {
  return (
    <>
      <SidebarDetailHeader title="Memory Monitor" />
      <SidebarBody>
        <div className={styles.loading}>Loading metrics...</div>
      </SidebarBody>
    </>
  )
}

function NoMetricsAvailable() {
  return (
    <>
      <SidebarDetailHeader title="Memory Monitor" />
      <SidebarBody>
        <div className={styles.error}>No metrics available</div>
      </SidebarBody>
    </>
  )
}
